"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Award, Clock, Wrench, PhoneCall, CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle, Star, Quote, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
const navienFaqs = [{
  id: "item-1",
  question: "What should I do if my Navien water heater displays Error Code E003 or E110?",
  answer: "Error E003 indicates an ignition failure, while E110 points to an abnormal exhaust or intake condition. First, verify your gas supply valve is fully open and check that the external vent terminations are free of debris or ice. If the error continues, power off the unit and call our 24/7 technicians to safely diagnose the gas valve, flame sensor, or pressure switch."
}, {
  id: "item-2",
  question: "How often does a Navien tankless water heater require maintenance?",
  answer: "Navien recommends a complete system flush and descaling every 12 to 18 months, depending on your water hardness in Clayton County. Annual service cleans the stainless steel heat exchangers, flushes internal sediment, and inspects the intake filter screen to preserve your 0.95+ UEF efficiency rating and factory warranty."
}, {
  id: "item-3",
  question: "Are your technicians certified to work on Navien NPE and NCB series?",
  answer: "Yes. Our master plumbers hold direct factory training on all Navien NPE-A2, NPE-S2 condensing tankless series, and NCB-H combi-boilers. We carry genuine OEM Navien replacement components on our emergency response trucks for fast same-day repairs."
}, {
  id: "item-4",
  question: "Can I replace my old standard tank with a Navien tankless system today?",
  answer: "In most residential homes, yes. Because Navien units utilize 2-inch PVC venting and offer 1/2-inch gas line compatibility up to 24 feet on select models, conversions are fast and often completed in a single working day without major home reconstruction."
}, {
  id: "item-5",
  question: "Will repairs affect my Navien manufacturer warranty?",
  answer: "No. As licensed and insured plumbing specialists using genuine factory parts, our repairs protect and uphold your original Navien warranty terms. We also provide documentation directly for your records and warranty validation."
}];
const trustStats = [{
  title: "15+ Years",
  subtitle: "Navien Unit Experience",
  detail: "Over a decade dedicated to tankless diagnostic and installation excellence."
}, {
  title: "OEM Parts",
  subtitle: "Stocked Mobile Trucks",
  detail: "Sensors, igniters, and valves ready for immediate 24/7 repair calls."
}, {
  title: "100% Certified",
  subtitle: "Licensed GA Plumbers",
  detail: "Fully insured master plumbing contractors serving Jonesboro and beyond."
}, {
  title: "< 60 Mins",
  subtitle: "Emergency Response",
  detail: "Rapid dispatch when your hot water fails or emergency leaks develop."
}];
const testimonials = [{
  name: "Marcus Holloway",
  role: "Homeowner in Jonesboro, GA",
  initials: "MH",
  comment: "Our Navien NPE-240A threw an ignition code late Saturday night. Jonesboro Emergency Plumber had a technician out within 45 minutes with the exact sensor needed. Outstanding professional service.",
  rating: 5
}, {
  name: "Angela Brooks",
  role: "Property Manager",
  initials: "AB",
  comment: "We rely on them for annual descaling and maintenance on four Navien combi units across our rental properties. Reliable, straightforward pricing, and true brand expertise.",
  rating: 5
}];
export default function NavienFaq() {
  return <section id="navienfaqandtrust" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground overflow-hidden">
      {/* Background Decorative Accent */}
      <div className="pointer-events-none absolute inset-0 -z-10 flex justify-center overflow-hidden">
        <div className="h-[450px] w-[900px] rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Heading */}
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 font-medium border border-border">
              <Sparkles className="size-3.5 text-accent" />
              <span>Certified Tankless Specialists</span>
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Navien Water Heater FAQ &amp; Certified Care
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Get clear answers about your Navien tankless system, error diagnostics, and regular maintenance from Jonesboro's trusted emergency plumbing team.
          </motion.p>
        </div>

        {/* Top Trust Pillars Grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-16">
          {trustStats.map((stat, i) => <motion.div key={stat.title} data-index={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.08
        }}>
              <Card className="h-full border border-border bg-card/60 backdrop-blur-xs transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-md">
                <CardHeader className="pb-2">
                  <div className="mb-2 flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    {i === 0 && <Clock className="size-5" />}
                    {i === 1 && <Wrench className="size-5" />}
                    {i === 2 && <ShieldCheck className="size-5" />}
                    {i === 3 && <Award className="size-5" />}
                  </div>
                  <CardTitle className="text-2xl font-bold text-foreground">
                    {stat.title}
                  </CardTitle>
                  <CardDescription className="text-sm font-semibold text-primary">
                    {stat.subtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    {stat.detail}
                  </p>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Main Content Grid: FAQ Accordion (Left) & Trust Details / Testimonials (Right) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* FAQ Accordion Column */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-border">
                <div className="flex size-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
                  <HelpCircle className="size-5" />
                </div>
                <div>
                  <h3 className="font-default text-xl font-bold text-foreground">
                    Frequently Asked Questions
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Answers to common Navien operational and troubleshooting questions
                  </p>
                </div>
              </div>

              <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
                {navienFaqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-b border-border py-1">
                    <AccordionTrigger className="text-left font-default text-base font-semibold text-foreground hover:text-primary transition-colors py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed pt-1 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>

          {/* Trust Cards, Testimonials & Direct CTA Column */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Certifications Card */}
            <Card className="border border-border bg-card shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-5 text-accent" />
                  <CardTitle className="text-lg font-bold">
                    Navien Factory Competency
                  </CardTitle>
                </div>
                <CardDescription>
                  Verified technician standards for Clayton County and Metro Atlanta
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-0 text-sm">
                <div className="flex items-start gap-2.5">
                  <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">
                    Trained on Navien NaviCirc recirculation valves and high-efficiency venting.
                  </span>
                </div>
                <div className="flex items-start gap-2.5">
                  <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">
                    Advanced digital analyzer diagnostics for precise gas-to-air combustion tuning.
                  </span>
                </div>
                <div className="flex items-start gap-2.5">
                  <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">
                    Warranty-safe factory descaling and heat exchanger restorations.
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Testimonials Card */}
            <div className="space-y-4">
              <h4 className="font-default text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Verified Customer Feedback
              </h4>
              {testimonials.map((review, i) => <div key={review.name} data-index={i} className="rounded-xl border border-border bg-card p-5 shadow-xs transition-[border-color] duration-200 hover:border-border/80">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1 text-accent">
                      {Array.from({
                    length: review.rating
                  }).map((_, starIndex) => <Star key={starIndex} data-index={starIndex} className="size-4 fill-accent" />)}
                    </div>
                    <Quote className="size-4 text-muted-foreground/40" />
                  </div>
                  <p className="text-sm text-foreground/90 italic leading-relaxed mb-4">
                    "{review.comment}"
                  </p>
                  <div className="flex items-center gap-3 border-t border-border/60 pt-3">
                    <SafeHydrate><Avatar className="size-8 bg-secondary text-secondary-foreground font-semibold text-xs">
                      <AvatarFallback>{review.initials}</AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div>
                      <p className="text-xs font-bold text-foreground">{review.name}</p>
                      <p className="text-[11px] text-muted-foreground">{review.role}</p>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Direct Call / Emergency Assist Box */}
            <div className="rounded-xl bg-primary p-6 text-primary-foreground shadow-md">
              <h4 className="font-default text-lg font-bold mb-2">
                Need Fast Navien Troubleshooting?
              </h4>
              <p className="text-xs md:text-sm text-primary-foreground/90 mb-5 leading-relaxed">
                Speak directly with an emergency technician in Jonesboro. We are standing by 24 hours a day with stocked service trucks.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild variant="secondary" className="w-full justify-center font-semibold bg-accent text-accent-foreground hover:bg-accent/90">
                  <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>Call 24/7 Dispatch</span>
                  </a>
                </Button>
                <Button asChild variant="outline" className="w-full justify-center border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                  <Link to="/contact/">Request Service</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}